<template>
  <div>
    <template v-if="dataLoaded">
      <b-container fluid>
        <b-row>
          <b-col class="pl-0">
            <div v-if="type == 'country'">
              <h1 class="mb-2">{{ this.country }}</h1>
              <h2 class="mb-2">Coordinators, trainers and groups</h2>
              <p class="text-primary-dark small mb-2">Last 12 months</p>
            </div>
            <div v-else-if="type == 'rc'">
              <h1 class="mb-2">{{ coordinators[this.id] }}</h1>
              <h2 class="mb-2">Trainers and groups</h2>
              <p class="text-primary-dark small mb-2">Last 12 months</p>
            </div>
          </b-col>
          <b-col class="pr-0">
            <b-table-simple borderless outlined small class="small">
              <b-tr><b-td colspan="2"><h3 class="text-secondary-mid">LEGEND</h3></b-td></b-tr>
              <b-tr><b-td class="text-right"><strong>Modules missed</strong></b-td><b-td>modules missed, counted against aggregate modules received so far by all teachers in the group</b-td></b-tr>
              <b-tr><b-td class="text-right"><strong>Trainer visits overdue</strong></b-td><b-td>module visits over 3 months ago / maintenance visits over 6 months ago</b-td></b-tr>
              <b-tr><b-td class="text-right"><strong>PPI avg</strong></b-td><b-td>average PPI for the last 3 visits to each school in a group</b-td></b-tr>
              <b-tr><b-td class="text-right"><strong>PPI avg / mod</strong></b-td><b-td>PPI per modules delivered (up to 12 modules)</b-td></b-tr>
            </b-table-simple>
          </b-col>
        </b-row>
      </b-container>
      <b-table borderless outlined class="small" small sort-by="sort_id" sticky-header="90vh" thead-tr-class="bg-secondary-mid text-white" :tbody-tr-class="rowClass" :fields="processedData.cols" :items="processedData.rows">
        <template #thead-top="data">
          <b-tr class="bg-secondary-mid text-white">
            <b-th class="border-right">Regional Coordinators</b-th>
            <b-th class="border-right">Groups</b-th>
            <b-th class="border-right">Teachers</b-th>
            <b-th class="border-right border-bottom" colspan="2">Modules delivered</b-th>
            <b-th class="border-right border-bottom" colspan="2">Modules missed</b-th>
            <b-th class="border-right border-bottom" colspan="3">Trainer visits (12 months)</b-th>
            <b-th class="border-right">RC visits</b-th>
            <b-th class="border-right border-bottom" colspan="2">PPI</b-th>
            <b-th class="border-bottom" colspan="2">Warnings</b-th>
          </b-tr>
        </template>
        <template #cell(name)="data">
          <span v-html="data.value"></span>
        </template>
        <template #cell(group)="data">
          <span v-html="data.value"></span>
        </template>
        <template #cell(teachers_schools)="data">
          <span v-html="data.value"></span>
        </template>
        <template #cell(modules)="data">
          <span v-if="typeof(data.value) === 'object'" v-b-tooltip.hover :title="Object.keys(data.value).sort().join('\r\n')">{{ Object.keys(data.value).length }}</span>
        </template>
        <template #cell(last_module_date)="data">
          {{ formatDate(data.value) }}
        </template>
      </b-table>
    </template>
    <h2 v-else-if="id && !dataLoaded" class="loading-placeholder">Loading...</h2>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MIOService from '../services/MIOService';
import { calculateHaversine, isDateMonthsAgo } from '@/utils/MioHelpers';

export default {
  name: 'ActivitySummary',
  props: {
    type: String,
    id: String,
  },
  data() {
    return {
      country: '',
      dataLoaded: false,
      observations: [],
      modules: [],
    }
  },
  created() {
    let limit = new Date();
    limit.setMonth(limit.getMonth() - 12);
    this.periodStartDate = limit;

    if (this.type == 'country') {
      this.country = this.id;
    }
    this.getData();
  },
  methods: {
    ...mapActions({
      GET_COORDINATORS: "coordinators/GET_COORDINATORS",
      GET_ALL_TRAINERS: "trainers/GET_ALL_TRAINERS",
      GET_ALL_GROUPS: "groups/GET_ALL_GROUPS",
    }),
    async getData() {
      try {
        const requests = {};

        if (this.type == 'country') {
          requests.observations = MIOService.getCountryRecentObservations;
          requests.modules = MIOService.getCountryModulesTaught;
        } else if (this.type == 'rc') {
          requests.observations = MIOService.getRCActiveTrainerRecentObservations;
          requests.modules = MIOService.getRCActiveTrainerTeacherModules;
        }
        const results = await Promise.all([
          this.GET_COORDINATORS(),
          this.GET_ALL_TRAINERS(),
          this.GET_ALL_GROUPS(),
          requests.observations(this.id),
          requests.modules(this.id),
        ]);
        this.observations = results[3].data.results;
        this.modules = results[4].data.results;
        this.dataLoaded = true;
      } catch (err) {
        console.error(err);
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return;
      return item.class;
    },
    formatGroupType(type) {
      if (type == 'Maintenance') {
        return 'mnt';
      } else if (type == 'Module') {
        return 'mod';
      } else {
        return '<strong class="text-warning">?????</strong>';
      }
    },
    formatDate(date) {
      let options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      return date.toLocaleString('default', options);
    },
    formatCoordinatorLink(id) {
      if (this.isAdmin) {
        return `<a href="/rc/trainer-activity?coord_id=${id}">${this.coordinators[id].toUpperCase()}</a>`;
      } else {
        return this.coordinators[id].toUpperCase();
      }
    },
    formatTrainerLink(id, coordId) {
      if (this.isAdmin) {
        return `<a data-id="${id}" href="/rc/trainer-activity?coord_id=${coordId}&trainer_name=${encodeURIComponent(this.trainers[id])}">${this.trainers[id]}</a>`;
      } else {
        return this.trainers[id];
      }
    },
    formatGroupLink(id, trainerId, coordId) {
      if (this.isAdmin) {
        return `<a href="/rc/trainer-activity?coord_id=${coordId}&trainer_name=${encodeURIComponent(this.trainers[trainerId])}&group_name=${encodeURIComponent(this.groups[id].name)}" data-group-uuid="${id}">${this.groups[id].name}</a>`;
      } else {
        return this.groups[id].name;
      }
    },
  },
  computed: {
    ...mapGetters({
      isAdmin: "auth/isAdmin",
    }),
    coordinators: {
      get() {
        if (!this.$store.state.coordinators.coordinators) { return []; }
        let coordinators = {};
        this.$store.state.coordinators.coordinators.forEach(coord => {
          coordinators[coord.uuid] = `${coord.first_name} ${coord.last_name}`;
        });
        return coordinators;
      },
    },
    trainers: {
      get() {
        if (!this.$store.state.trainers.trainers) { return []; }
        let trainers = {};
        this.$store.state.trainers.trainers.forEach(trainer => {
          trainers[trainer.uuid] = `${trainer.first_name} ${trainer.last_name}`;
        });
        return trainers;
      },
    },
    groups: {
      get() {
        if (!this.$store.state.groups.groups) { return []; }
        let groups = {};
        this.$store.state.groups.groups.forEach(group => {
          groups[group.uuid] = {
            name: group.group_name,
            type: group.type,
            end_date: group.end_date,
          };
        });
        return groups;
      },
    },
    processedData: {
      get() {
        let modulesData = {},
          obsData = {},
          output = {
            cols: [
              { key: 'name', label: '& Trainers', class: 'border-right' },
              { key: 'group', label: '', class: 'border-right' },
              { key: 'teachers_schools', label: '(Schools)', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'modules', label: 'no.', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'last_module_date', label: 'latest', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'modules_missed', label: 'no.', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'modules_missed_pc', label: '%', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'trainer_visits', label: 'total', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'overdue_visits', label: 'overdue', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'overdue_visits_pc', label: '% overdue', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'rc_visits', label: '', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'average_ppi', label: 'ave', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'ppi_per_module', label: '/ mod', class: 'text-right', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'data_warnings', label: 'data', class: 'text-center', thClass: 'font-weight-normal border-right', tdClass: 'border-right' },
              { key: 'location_warnings', label: 'geo', class: 'text-center', thClass: 'font-weight-normal' },
            ],
            rows: [],
          },
          corners = ['work_displayed', 'play_shop', 'nature_box', 'dress_up_box', 'music', 'arts', 'reading_corner', 'matching_games', 'puzzles', 'book_making', 'conservation', 'gardening'];

        // Process modules
        this.modules.forEach(entry => {
          entry.teachers_uuid.forEach(teacher_uuid => {
            if (!modulesData[teacher_uuid]) {
              modulesData[teacher_uuid] = {
                names: {},
                last_module_date: false,
              };
            }
            modulesData[teacher_uuid].names[entry.course] = '';
            let moduleDate = new Date(entry.date);
            if (!modulesData[teacher_uuid].last_module_date || moduleDate > modulesData[teacher_uuid].last_module_date.getTime()) {
              modulesData[teacher_uuid].last_module_date = moduleDate;
            }
          });
        });

        // Process observations
        this.observations.forEach(obs => {

          let coordId = false;
          if (this.type == 'country') {
            coordId = obs.coordinator_uuid;
          } else if (this.type == 'rc') {
            coordId = this.id;
          }
          if (!coordId) { return; }

          if (!obsData[coordId]) {
            obsData[coordId] = {
              trainers: {},
              mod_group_count: 0,
              mnt_group_count: 0,
              teachers_count: 0,
              schools_count: 0,
              modules_missed_count: 0,
              modules_missed_pc: 0,
              possible_modules_count: 0,
              visits_count: 0,
              overdue_visits_count: 0,
              covisits_count: 0,
              data_warnings: 0,
              location_warnings: 0,
              schools_not_geolocated: {},
            };
          }

          if (obs.trainer_uuid === 'NULL' || !obs.trainer_uuid
            || obs.teacher_uuid === 'NULL' || !obs.teacher_uuid
            || obs.school_uuid === 'NULL' || !obs.school_uuid
            || obs.group_uuid === 'NULL' || !obs.group_uuid) {
            obsData[coordId].data_warnings++; // matches RC warning: obs_field_missing
            return;
          }

          if (!obsData[coordId].trainers[obs.trainer_uuid]) {
            obsData[coordId].trainers[obs.trainer_uuid] = {
              groups: {},
            };
          }

          if (!obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid]) {
            obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid] = {
              visits_count: 0,
              covisits_count: 0,
              last_module_date: false,
              modules: {},
              schools: {},
              teachers: {},
              data_warnings: 0,
              location_warnings: 0,
              schools_not_geolocated: {},
            };

            if (this.groups[obs.group_uuid]) {
              if (this.groups[obs.group_uuid].type == 'Maintenance') {
                obsData[coordId].mnt_group_count++;
              } else if (this.groups[obs.group_uuid].type == 'Module') {
                obsData[coordId].mod_group_count++;
              }
            } else {
              obsData[coordId].data_warnings++; // matches RC warning: obs_group_not_found
              obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].data_warnings++;
            }
          }

          if (!obs.obs_lat || !obs.obs_lng) {
            obsData[coordId].location_warnings++; // matches RC warning: obs_not_geolocated
            obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].location_warnings++;
          } else if (obs.school_lat && obs.school_lng) {
            let distance = calculateHaversine([obs.school_lat, obs.school_lng], [obs.obs_lat, obs.obs_lng]);
            if (distance > 1) {
              obsData[coordId].location_warnings++; // matches RC warning: obs_school_mismatch
              obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].location_warnings++;
            }
          }

          if (obs.type != this.groups[obs.group_uuid].type) {
            obsData[coordId].data_warnings++; // matches RC warning: type_mismatch
            obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].data_warnings++;
          }

          if (!obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].schools[obs.school_uuid]) {
            obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].schools[obs.school_uuid] = {};
            obsData[coordId].schools_count++;

            if (!obs.school_lat || !obs.school_lng) {
              obsData[coordId].schools_not_geolocated[obs.school_uuid] = true;
              obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].schools_not_geolocated[obs.school_uuid] = true;
            }
          }

          if (!obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].teachers[obs.teacher_uuid]) {
            obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].teachers[obs.teacher_uuid] = {
              visits: [],
            };
            obsData[coordId].teachers_count++;
          }

          if (modulesData[obs.teacher_uuid]) {
            Object.assign(obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].modules, modulesData[obs.teacher_uuid].names);
            if (!obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].last_module_date || modulesData[obs.teacher_uuid].last_module_date.getTime() > obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].last_module_date.getTime()) {
              obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].last_module_date = modulesData[obs.teacher_uuid].last_module_date;
            }
          }

          if (obs.coordinator_present) {
            obsData[coordId].covisits_count++;
            obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].covisits_count++
          }

          obsData[coordId].visits_count++;
          obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].visits_count++;
          let cornersCount = corners.reduce(((total, corner) => obs[corner] ? ++total : total), 0);

          obsData[coordId].trainers[obs.trainer_uuid].groups[obs.group_uuid].teachers[obs.teacher_uuid].visits.push({
            date: new Date(obs.observation_date),
            corners_count: cornersCount,
            school_teacher_active: obs.school_teacher_active,
          });
        });

        // Assemble table
        Object.keys(obsData).forEach(coordinator_uuid => {
          if (!this.coordinators[coordinator_uuid]) { return; }

          // Add non-geolocated schools to warnings total
          obsData[coordinator_uuid].location_warnings += Object.keys(obsData[coordinator_uuid].schools_not_geolocated).length;

          let trainerRows = [], RCTotalPPI = 0, RCTotalPPIPerModule = 0, RCGroupCount = 0;
          Object.keys(obsData[coordinator_uuid].trainers).forEach(trainer_uuid => {

            let trainerGroupCount = 0;
            Object.keys(obsData[coordinator_uuid].trainers[trainer_uuid].groups).forEach(group_uuid => {

              let group = obsData[coordinator_uuid].trainers[trainer_uuid].groups[group_uuid],
                groupEndDate = new Date(this.groups[group_uuid].end_date);

              group.location_warnings += Object.keys(group.schools_not_geolocated).length;

              let maxModules = Object.keys(group.modules).length;

              let modulesMissed = Object.keys(group.teachers).reduce((total, teacher_uuid) => {
                if (!modulesData[teacher_uuid]) { return total; }
                let teacherModules = Object.keys(modulesData[teacher_uuid].names).length;
                let missingModules = Math.max(0, maxModules - teacherModules);
                total += missingModules;
                return total;
              }, 0);
              obsData[coordinator_uuid].modules_missed_count += modulesMissed;
              let possibleModules = Object.keys(group.teachers).length * maxModules;
              obsData[coordinator_uuid].possible_modules_count += possibleModules;

              let overdueVisits = 0;

              if (groupEndDate > this.periodStartDate || this.groups[group_uuid].type == 'Maintenance') { // Only record overdue visits if group ends in or after current period (Maintenance groups end date is not relevant)
                overdueVisits = Object.keys(group.teachers).reduce((total, teacher_uuid) => {
                  const visit = group.teachers[teacher_uuid].visits[0];
                  if (!visit.school_teacher_active) { return total; }
                  if (this.groups[group_uuid].type == 'Maintenance' && isDateMonthsAgo(visit.date, 6)) {
                    return total + 1;
                  } else if (this.groups[group_uuid].type == 'Module' && isDateMonthsAgo(visit.date)) {
                    return total + 1;
                  }
                  return total;
                }, 0);
                obsData[coordinator_uuid].overdue_visits_count += overdueVisits;
              }

              let cornerCount = 0, visitsCount = 0;
              Object.keys(group.teachers).forEach(teacher_uuid => {
                let lastThreeVisits = group.teachers[teacher_uuid].visits.slice(0, 3);
                let teacherCorners = lastThreeVisits.reduce((total, obs) => {
                  total += obs.corners_count;
                  return total;
                }, 0);
                cornerCount += teacherCorners;
                visitsCount += lastThreeVisits.length;
              });
              let averagePPI = cornerCount / visitsCount;
              if (maxModules > 12) {
                maxModules = 12;
              }
              let PPIPerModule = Object.keys(group.modules).length ? averagePPI / maxModules : 0;

              RCTotalPPI += averagePPI;
              RCTotalPPIPerModule += PPIPerModule;

              trainerRows.push({
                sort_id: `${encodeURIComponent(this.coordinators[coordinator_uuid])}_${encodeURIComponent(this.trainers[trainer_uuid])}`,
                name: trainerGroupCount == 0 ? `&angrt;&nbsp;<strong>T&nbsp;&nbsp;${this.formatTrainerLink(trainer_uuid, coordinator_uuid)}</strong>` : '',
                class: `border-top ${trainerGroupCount == 0 ? 'border-grey' : 'border-light'}`,
                group: `${this.formatGroupType(this.groups[group_uuid].type)}&nbsp;<strong>${this.formatGroupLink(group_uuid, trainer_uuid, coordinator_uuid)}</strong>`,
                teachers_schools: `<strong>${Object.keys(group.teachers).length}</strong> (${Object.keys(group.schools).length})`,
                modules: group.modules,
                last_module_date: group.last_module_date || '—',
                modules_missed: modulesMissed,
                modules_missed_pc: possibleModules ? `${(100 * modulesMissed / possibleModules).toFixed(0)}%` : '—',
                trainer_visits: group.visits_count,
                overdue_visits: overdueVisits,
                overdue_visits_pc: `${(100 * overdueVisits / Object.keys(group.teachers).length).toFixed(0)}%`,
                rc_visits: group.covisits_count,
                average_ppi: averagePPI ? averagePPI.toFixed(1) : '—',
                ppi_per_module: PPIPerModule ? PPIPerModule.toFixed(1) : '—',
                data_warnings: group.data_warnings,
                location_warnings: group.location_warnings,
              });
              trainerGroupCount++;
              RCGroupCount++;
            });
          });

          let averagePPI = 0, averagePPIPerModule = 0;
          if (RCGroupCount > 0) {
            averagePPI = RCTotalPPI / RCGroupCount;
            averagePPIPerModule = RCTotalPPIPerModule / RCGroupCount;
          }

          output.rows = output.rows.concat([{
            class: 'bg-grey-light font-weight-bold',
            sort_id: `${encodeURIComponent(this.coordinators[coordinator_uuid])}`,
            name: `RC&nbsp;&nbsp;${this.formatCoordinatorLink(coordinator_uuid)} (${Object.keys(obsData[coordinator_uuid].trainers).length} trainers)`,
            group: `${obsData[coordinator_uuid].mod_group_count} mod / ${obsData[coordinator_uuid].mnt_group_count} mnt`,
            teachers_schools: `${obsData[coordinator_uuid].teachers_count}</strong> (${obsData[coordinator_uuid].schools_count})`,
            modules_missed: obsData[coordinator_uuid].modules_missed_count,
            modules_missed_pc: `${(100 * obsData[coordinator_uuid].modules_missed_count / obsData[coordinator_uuid].possible_modules_count).toFixed(0)}%`,
            trainer_visits: obsData[coordinator_uuid].visits_count,
            overdue_visits: obsData[coordinator_uuid].overdue_visits_count,
            overdue_visits_pc: `${(100 * obsData[coordinator_uuid].overdue_visits_count / obsData[coordinator_uuid].teachers_count).toFixed(1)}%`,
            rc_visits: obsData[coordinator_uuid].covisits_count,
            average_ppi: averagePPI ? averagePPI.toFixed(1) : '—',
            ppi_per_module: averagePPIPerModule ? averagePPIPerModule.toFixed(1) : '—',
            data_warnings: obsData[coordinator_uuid].data_warnings,
            location_warnings: obsData[coordinator_uuid].location_warnings,
          }]).concat(trainerRows);
        });

        // console.log(obsData);
        return output;
      }
    },
  },
};
</script>

<style>
.b-tooltip {
  white-space: pre-wrap;
}

.table.b-table > thead > tr > .table-b-table-default {
  background-color: inherit;
  color: inherit;
}

.b-table-sticky-header > .table.b-table > thead > tr:nth-of-type(2) > th {
  position: relative;
}
</style>
